import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || "https://contractcheck360-production.up.railway.app"}/api`;

const getAuthHeader = () => {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Contracts
  uploadContract: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_BASE}/contracts/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getContracts: async () => {
    const response = await axios.get(`${API_BASE}/contracts`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  analyzeContract: async (contractId) => {
    const response = await axios.post(
      `${API_BASE}/contracts/analyze`,
      { contract_id: contractId },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  compareContracts: async (contractIds) => {
    const response = await axios.post(
      `${API_BASE}/contracts/compare-detailed`,
      { contract_ids: contractIds },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Payments
  createCheckout: async (packageId, metadata = {}) => {
    const originUrl = window.location.origin;
    const response = await axios.post(
      `${API_BASE}/payments/create-checkout`,
      { package_id: packageId, origin_url: originUrl, metadata },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getPaymentStatus: async (sessionId) => {
    const response = await axios.get(`${API_BASE}/payments/status/${sessionId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getPaymentHistory: async () => {
    const response = await axios.get(`${API_BASE}/payments/history`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Reports
  downloadPDF: async (contractId) => {
    const response = await axios.get(`${API_BASE}/reports/pdf/${contractId}`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },

  downloadExcel: async (contractId) => {
    const response = await axios.get(`${API_BASE}/reports/excel/${contractId}`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },

  // Chatbot
  askChatbot: async (message) => {
    const response = await axios.post(`${API_BASE}/chatbot/ask`, { message }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Admin
  getAdminStats: async () => {
    const response = await axios.get(`${API_BASE}/admin/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllUsers: async () => {
    const response = await axios.get(`${API_BASE}/admin/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Dashboard Stats
  getDashboardStats: async () => {
    const response = await axios.get(`${API_BASE}/dashboard/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Contract Detail with Risk
  getContractDetail: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/detail/${contractId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Risk Analysis
  getRiskAnalysis: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/${contractId}/risk-analysis`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Detailed Comparison
  compareContractsDetailed: async (contractIds) => {
    const response = await axios.post(
      `${API_BASE}/contracts/compare-detailed`,
      { contract_ids: contractIds },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // ==================== CLM (Contract Lifecycle Management) ====================
  getContractFull: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/${contractId}/full`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateContractMetadata: async (contractId, metadata) => {
    const response = await axios.patch(
      `${API_BASE}/contracts/${contractId}/metadata`,
      metadata,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  deleteContract: async (contractId) => {
    const response = await axios.delete(`${API_BASE}/contracts/${contractId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  downloadContract: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/${contractId}/download`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },

  getContractHistory: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/${contractId}/history`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ==================== Google Calendar ====================
  getGCalLinks: async (contractId) => {
    const response = await axios.get(`${API_BASE}/contracts/${contractId}/gcal`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ==================== Comparisons ====================
  getComparisons: async () => {
    const response = await axios.get(`${API_BASE}/comparisons`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  downloadComparisonPdf: async (comparisonId) => {
    const response = await axios.get(`${API_BASE}/comparisons/${comparisonId}/pdf`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },

  // ==================== Alerts ====================
  getAlerts: async () => {
    const response = await axios.get(`${API_BASE}/alerts`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAlertsCount: async () => {
    const response = await axios.get(`${API_BASE}/alerts/count`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ==================== User Profile ====================
  getProfile: async () => {
    const response = await axios.get(`${API_BASE}/profile`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await axios.patch(`${API_BASE}/profile`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await axios.post(
      `${API_BASE}/profile/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // ==================== Notifications ====================
  getNotificationPreferences: async () => {
    const response = await axios.get(`${API_BASE}/profile/notifications`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  toggleNotifications: async () => {
    const response = await axios.patch(`${API_BASE}/profile/notifications`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Password Reset
  forgotPassword: async (email) => {
    const response = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await axios.get(`${API_BASE}/auth/verify-reset-token?token=${token}`);
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await axios.post(`${API_BASE}/auth/reset-password`, { token, password });
    return response.data;
  },

  // ==================== B2B Organization ====================
  createOrg: async (name) => {
    const response = await axios.post(`${API_BASE}/orgs`, { name }, { headers: getAuthHeader() });
    return response.data;
  },

  getMyOrg: async () => {
    const response = await axios.get(`${API_BASE}/orgs/my`, { headers: getAuthHeader() });
    return response.data;
  },

  inviteMember: async (email) => {
    const response = await axios.post(`${API_BASE}/orgs/invite`, { email }, { headers: getAuthHeader() });
    return response.data;
  },

  removeMember: async (userId) => {
    const response = await axios.delete(`${API_BASE}/orgs/members/${userId}`, { headers: getAuthHeader() });
    return response.data;
  },

  getOrgContracts: async () => {
    const response = await axios.get(`${API_BASE}/orgs/contracts`, { headers: getAuthHeader() });
    return response.data;
  }
};

export default api;
